/**
 * Spend the prop cap by DECLARED PRECEDENCE, after everything is placed.
 *
 * The cap is a curation rule — the literature's "6-9 props, and the failure
 * mode is too many" — not a geometric one, so it is the last word rather than
 * a running balance. It used to be the latter, and the two orders disagree:
 * props are PLACED in stage order (a tray before the things that stand on it,
 * soap before the free row) while priority is the PROGRAMME's order, and the
 * stages do not follow it. `staging-stems` is declared dead last, precisely
 * because it is the most droppable prop in the kit, but it is placed in the
 * surface stage — so on a well-furnished bathroom it spent the ninth slot and
 * the towel basket and the shower-niche bottles, both declared above it, were
 * never attempted at all. Measured on the corpus: 7 rooms.
 *
 * Trimming at the end needs no reservation guesswork, which is the other way
 * to fix this and the wrong one: a slot held back for a niche the room does
 * not have is a prop lost for nothing.
 *
 * Two keys, in this order:
 *   - `optional` first. This is what the flag has always promised and never
 *     delivered — it was declared on four props and read by nothing.
 *   - then rank, lowest priority first.
 *
 * A prop STANDING ON another goes with its host (the candle and jar on a
 * tray): a tray cut from the plan leaves them in mid-air. Dependents are
 * followed transitively, so this can land under the cap rather than on it —
 * correct, and much cheaper than choosing a victim that avoids it.
 *
 * Shared by every room planner; each passes its own cap. `fits` is an extra
 * budget a planner may impose on the whole set (a kitchen's "at least 70% of
 * the counter stays bare"): while it returns false, the worst prop goes, by the
 * same keys as the cap — but only among props `spends(entry)` says count
 * toward it. A tea towel hanging on the oven takes no counter, and cutting it
 * to make room on the counter frees nothing. `counts(entry)` does the same for
 * the CAP: a kitchen's cap is a counter rule ("1-2 styled items per counter
 * run"), so its one piece of wall art is outside it.
 */

const { MAX_PROPS_PER_BATHROOM } = require('./constants');

/**
 * @param {Object[]} entries  placed props; trimmed IN PLACE and returned
 * @param {Map<Object,{rank:number, optional:boolean}>} curation
 * @param {(type: string, reason: string) => void} drop
 * @param {Object} [opts]
 * @param {number} [opts.maxProps]  defaults to the bathroom cap
 * @param {(entries: Object[]) => boolean} [opts.fits]
 * @param {(entry: Object) => boolean} [opts.spends]  which entries `fits` measures
 * @param {(entry: Object) => boolean} [opts.counts]  which entries the cap counts
 */
function trimToBudget(entries, curation, drop, opts = {}) {
  const maxProps = opts.maxProps ?? MAX_PROPS_PER_BATHROOM;
  const fits = typeof opts.fits === 'function' ? opts.fits : () => true;
  const spends = typeof opts.spends === 'function' ? opts.spends : () => true;
  const counts = typeof opts.counts === 'function' ? opts.counts : () => true;
  const counted = () => entries.filter(counts).length;
  // An entry with no recorded programme line is treated as the most
  // droppable thing in the room rather than the least. Nothing mints one
  // today; if something ever does, cutting it is the safe way to find out.
  const keyOf = (e) => {
    const k = curation.get(e) || { rank: Infinity, optional: true };
    return { optional: k.optional ? 1 : 0, rank: k.rank };
  };
  while (counted() > maxProps || (entries.length && !fits(entries))) {
    const overCap = counted() > maxProps;
    const reason = overCap ? 'over-the-prop-budget' : 'over-the-surface-budget';
    // The optional props go first; within each group, lowest priority first.
    let victim = null;
    let victimKey = null;
    for (const e of entries) {
      if (overCap ? !counts(e) : !spends(e)) continue;
      const k = keyOf(e);
      const worse =
        !victimKey ||
        k.optional > victimKey.optional ||
        (k.optional === victimKey.optional && k.rank > victimKey.rank);
      if (worse) {
        victim = e;
        victimKey = k;
      }
    }
    if (!victim) break;
    const doomed = new Set([victim.id]);
    for (let grew = true; grew; ) {
      grew = false;
      for (const e of entries) {
        if (doomed.has(e.id)) continue;
        if ((e.parents || []).some((parent) => doomed.has(parent.id))) {
          doomed.add(e.id);
          grew = true;
        }
      }
    }
    for (let i = entries.length - 1; i >= 0; i--) {
      if (!doomed.has(entries[i].id)) continue;
      drop(entries[i].type, reason);
      entries.splice(i, 1);
    }
  }
  return entries;
}

module.exports = { trimToBudget };
